import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, FileText, Clock, Gavel, ArrowRight } from "lucide-react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { HealthGauge, StatCard, StatusBadge } from "../../components/app/widgets";
import { Button } from "../../components/ui/button";

export default function ClientDashboard() {
  const { t, lang, dir } = useI18n();
  const { org } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.get("/dashboard/client").then((r) => setD(r.data)).catch(() => {});
    api.get("/health-score").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="text-muted-foreground">{t("loading")}</div>;
  if (org && !org.onboarded) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("onb_title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("onb_sub")}</p>
        <Button data-testid="go-onboarding" onClick={() => nav("/app/onboarding")} className="mt-6 rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">{t("save_profile")}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 gc-fade-up" data-testid="client-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("welcome")}, {org?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("secondary_pos")}</p>
        </div>
        <Button data-testid="dash-new-request" onClick={() => nav("/app/new")} className="rounded-full bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">
          {t("nav_new_request")} <ArrowRight className="ms-2 h-4 w-4" style={{ transform: dir === "rtl" ? "scaleX(-1)" : "none" }} />
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-2xl border border-border bg-card p-6 flex flex-col items-center justify-center gc-ring-shadow">
          <div className="text-sm font-medium text-muted-foreground mb-3">{t("vat_health_score")}</div>
          <HealthGauge score={d.health_score} />
          <div className="text-xs text-muted-foreground mt-3 text-center max-w-[220px]">{t("diag_disclaimer")}</div>
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
          <StatCard testid="stat-open-issues" label={t("open_issues")} value={d.open_issues} accent="hsl(24 90% 50%)" />
          <StatCard testid="stat-high-risk" label={t("high_risk")} value={d.high_risk} accent="hsl(354 78% 54%)" />
          <StatCard testid="stat-awaiting-docs" label={t("awaiting_documents")} value={d.awaiting_documents} accent="hsl(38 92% 45%)" />
          <StatCard testid="stat-next-filing" label={t("next_filing")} value={`${d.next_deadline_days} ${t("days")}`} accent="hsl(158 64% 28%)" />
          <StatCard testid="stat-open-cases" label={t("open_cases")} value={d.open_cases} />
          <StatCard testid="stat-zatca" label={t("zatca_cases")} value={d.zatca_cases} accent="hsl(217 63% 15%)" />
        </div>
      </div>

      {health && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="font-semibold text-[hsl(217_63%_15%)] mb-4">{t("vat_health_score")}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {health.categories.map((c) => (
              <div key={c.key} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className="text-sm">{c.key}</span>
                <span className="font-mono text-sm font-bold" style={{ color: c.score >= 85 ? "hsl(152 76% 32%)" : c.score >= 70 ? "hsl(158 64% 32%)" : c.score >= 55 ? "hsl(38 92% 45%)" : "hsl(354 78% 54%)" }}>{c.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-[hsl(217_63%_15%)]">{t("recent_cases")}</div>
          <button onClick={() => nav("/app/cases")} className="text-sm text-[hsl(158_64%_28%)]">{t("view_all")}</button>
        </div>
        <div className="space-y-2">
          {d.recent_cases.length === 0 && <div className="text-sm text-muted-foreground">{t("no_cases")}</div>}
          {d.recent_cases.map((c) => (
            <button key={c.id} data-testid={`recent-case-${c.id}`} onClick={() => nav(`/app/cases/${c.id}`)}
              className="w-full flex items-center justify-between rounded-lg border border-border px-4 py-3 hover:border-[hsl(158_64%_28%)] transition-colors text-start">
              <div>
                <div className="font-mono text-xs text-muted-foreground">{c.reference}</div>
                <div className="text-sm font-medium mt-0.5">{lang === "ar" ? c.service_label_ar : c.service_label_en}</div>
              </div>
              <StatusBadge status={c.status} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
